/**
 * "Listen" for words, expressions and transcript lines on the moment screen.
 * Real mode: the server reads the phrase with Gemini TTS and caches the WAV.
 * Mock mode, no allowance left (402) or any server failure: the browser's own
 * speech synthesis, in the target language.
 * Only one phrase plays at a time; calling speak() again stops the previous one.
 * `speakAll` plays several back to back (v1 ticket 31); any other listen or stop ends it. `speakAndWait` plays one
 * and waits for it; `chainedVoice` does the same through one reusable element (an imagined scene's Your turn).
 */
import { api } from './api';
import { currentTarget, pairFor } from './i18n';
import { lineAt, lineStarts } from './scene-lines';

let current: HTMLAudioElement | null = null;
let listeners = new Set<(playing: string | null, heard: boolean) => void>();
let playingText: string | null = null;
/** Whether what is named playing is heard yet (its audio playing, or the device voice speaking), not only asked for. */
let playingHeard = false;
/** Bumped by every listen or stop from outside a chain, so a running `speakAll` knows to end. */
let chain = 0;

function notify(text: string | null, heard = false) {
  playingText = text;
  playingHeard = text !== null && heard;
  for (const l of listeners) l(text, playingHeard);
}

/** Names `text` heard, once its sound has actually started (and it is still the one named). */
function heardNow(text: string) {
  if (playingText === text && !playingHeard) notify(text, true);
}

/**
 * Subscribe to "what is playing" changes (for button state): the text named, and whether it is heard yet. A
 * phrase is named as soon as it is asked for (`heard` false while its clip is made: a button shows it preparing),
 * then again with `heard` true once its sound starts. Returns an unsubscribe function.
 */
export function onSpeaking(l: (playing: string | null, heard: boolean) => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function stopSpeaking() {
  chain++;
  stopNow();
}

function stopNow() {
  if (current) { current.pause(); current = null; }
  if ('speechSynthesis' in window) speechSynthesis.cancel();
  notify(null);
}

/**
 * `onStart` fires when the phrase is actually heard, not when it is asked for (the first play waits for the clip to
 * be made): with the playing element, whose clock a caller can follow, or null for the device voice.
 */
export async function speak(
  momentId: string, text: string, opts: { mock?: boolean; lang?: string; onStart?: (audio: HTMLAudioElement | null) => void } = {},
) {
  chain++;
  return speakUrl(api.ttsUrl(momentId, text), text, opts);
}

/**
 * Plays `texts` one after another through the same cached route and fallback as `speak`, calling `before(i)` as
 * each one starts. `onStart` fires once, when the first line is actually heard (its audio playing, or the device
 * voice starting). Resolves when the last has played or anything else was played or stopped meanwhile.
 */
export async function speakAll(
  momentId: string, texts: readonly string[], opts: { mock?: boolean; lang?: string; onStart?: () => void } = {}, before?: (i: number) => void,
) {
  const mine = ++chain;
  stopNow();
  const { onStart, ...rest } = opts;
  for (let i = 0; i < texts.length && chain === mine; i++) {
    before?.(i);
    void speakUrl(api.ttsUrl(momentId, texts[i]), texts[i], i === 0 ? { ...rest, onStart } : rest);
    // speakUrl names the phrase before its first await; it has played once anything else, or nothing, is named.
    await new Promise<void>((resolve) => {
      if (playingText !== texts[i]) { resolve(); return; }
      const off = onSpeaking((now) => { if (now !== texts[i]) { off(); resolve(); } });
    });
  }
}

/** How long a phrase may take on the device voice before a wait on it gives up (some never say they ended). */
export function readingPaceMs(text: string): number {
  return Math.max(2500, 70 * [...text].length);
}

/**
 * Plays one phrase like `speak` and resolves once it has played: `true` when it did, `false` when anything else was
 * played or stopped meanwhile (the caller's sequence should end). The device voice's wait is capped at
 * `readingPaceMs`, since a voice that never fires its end (headless Chrome) would otherwise hold it forever.
 */
export async function speakAndWait(momentId: string, text: string, opts: { mock?: boolean; lang?: string } = {}): Promise<boolean> {
  const mine = ++chain;
  stopNow();
  await new Promise<void>((resolve) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let off = () => {};
    const finish = () => { clearTimeout(timer); off(); resolve(); };
    void speakUrl(api.ttsUrl(momentId, text), text, { ...opts, onDevice: () => { timer = setTimeout(finish, readingPaceMs(text)); } });
    // speakUrl names the phrase before its first await (and its own stop names nothing before that): listen after it.
    if (playingText !== text) { finish(); return; }
    off = onSpeaking((now) => { if (now !== text) finish(); });
  });
  return chain === mine;
}

/**
 * A voice for a chain of lines started by taps (an imagined scene's Your turn): every line plays through one audio
 * element, its `src` swapped per line. Safari lets an element play outside a tap once it has played inside one, so
 * only a `say` made in a tap needs to be called from it; `say` calls `play()` before any await. Otherwise like
 * `speakAndWait`: resolves `true` once the line has played, `false` when anything else was played or stopped
 * meanwhile. Mock mode, 402, 501 or any failure reads the line with the device voice (capped at `readingPaceMs`).
 */
export function chainedVoice() {
  let el: HTMLAudioElement | null = null;
  return {
    say(momentId: string, text: string, opts: { mock?: boolean; lang?: string } = {}): Promise<boolean> {
      if (opts.mock) return speakAndWait(momentId, text, opts);
      const mine = ++chain;
      stopNow();
      const a = (el ??= new Audio());
      a.src = api.ttsUrl(momentId, text);
      current = a;
      notify(text);
      return new Promise<'ended' | 'stopped' | 'failed'>((resolve) => {
        let settled = false;
        const end = (how: 'ended' | 'stopped' | 'failed') => {
          if (settled) return;
          settled = true;
          a.onended = a.onerror = a.onpause = a.onplaying = null;
          resolve(how);
        };
        a.onended = () => end('ended');
        a.onplaying = () => { if (current === a) heardNow(text); };
        // 402 and 501 send JSON, not audio: the element errors and the device voice reads it.
        a.onerror = () => end('failed');
        // Stopped from here, or paused by the system (a call): the end of a clip pauses it too, after `ended` is set.
        a.onpause = () => { if (!a.ended) end('stopped'); };
        a.play().catch(() => end('failed'));
      }).then((how) => {
        const still = current === a;
        if (still) current = null;
        if (chain !== mine) return false;
        if (how === 'failed') return speakAndWait(momentId, text, { ...opts, mock: true });
        if (still) notify(null);
        return how === 'ended';
      });
    },
  };
}

/** What `onSpeaking` names while a whole imagined scene plays from its one clip. */
export const SCENE_PLAYING = '\u0000scene';

/**
 * Plays an imagined scene: its one two-voice clip from the scene-audio route, calling `before(i)` as each line is
 * estimated to start (`scene-lines.ts`). When the clip can't play (402 with the allowance spent, 501 in mock mode,
 * a failed call), the lines play one by one through `speakAll` instead, with the same `before`. Resolves when the
 * scene has played or anything else was played or stopped meanwhile.
 */
export async function playScene(
  momentId: string, texts: readonly string[], opts: { mock?: boolean; lang?: string; onPlaying?: () => void } = {}, before?: (i: number) => void,
) {
  const mine = ++chain;
  stopNow();
  if (opts.mock) return speakAll(momentId, texts, { mock: opts.mock, lang: opts.lang, onStart: opts.onPlaying }, before);
  // No await before play(): iOS gives the tap's permission to play only to a call made in the tap itself.
  const starts = lineStarts(texts);
  let reached = -1;
  // Counted outside the call: `before?.(++reached)` would skip the increment with no `before`, and never end.
  const reach = (k: number) => {
    while (reached < k && reached + 1 < texts.length) {
      reached++;
      before?.(reached);
    }
  };
  const a = new Audio(api.sceneAudioUrl(momentId));
  current = a;
  notify(SCENE_PLAYING);
  const how = await new Promise<'ended' | 'paused' | 'failed'>((resolve) => {
    a.ontimeupdate = () => { if (current === a) reach(lineAt(starts, a.currentTime, a.duration)); };
    a.onplaying = () => { if (current === a) { heardNow(SCENE_PLAYING); opts.onPlaying?.(); } };
    a.onended = () => { resolve('ended'); };
    a.onerror = () => { resolve('failed'); };
    // Stopped from here, or paused by the system (a call, another app): the lines not reached were not heard.
    a.onpause = () => { if (!a.ended) resolve('paused'); };
    a.play().catch(() => resolve('failed'));
  });
  const mineStill = current === a;
  if (mineStill) current = null;
  if (chain !== mine) return;
  if (how !== 'failed') {
    if (how === 'ended') reach(texts.length - 1);
    if (mineStill) notify(null);
    return;
  }
  // Nothing (or only the start) was heard: the lines one by one, counted from the first again.
  await speakAll(momentId, texts, { mock: opts.mock, lang: opts.lang, onStart: opts.onPlaying }, before);
}

/**
 * Placement listen question: general TTS (no moment yet). Same singleton,
 * stop-previous and speechSynthesis fallback as moment phrases.
 * `playMs` bounds server-TTS stalls: on timeout the speechSynthesis fallback speaks instead.
 */
export function speakGeneral(text: string, opts: { mock?: boolean; lang?: string; playMs?: number } = {}) {
  chain++;
  return speakUrl(api.ttsGeneralUrl(text), text, opts);
}

async function speakUrl(
  url: string, text: string,
  opts: { mock?: boolean; lang?: string; playMs?: number; onStart?: (audio: HTMLAudioElement | null) => void; onDevice?: () => void } = {},
) {
  if (playingText === text) { stopNow(); return; }
  let started = false;
  const start = (audio: HTMLAudioElement | null = null) => {
    heardNow(text);
    if (!started) { started = true; opts.onStart?.(audio); }
  };
  stopNow();
  notify(text);
  if (!opts.mock) {
    try {
      const a = new Audio(url);
      current = a;
      // A server that answers 501 (mock mode) or 402 (no AI allowance left)
      // sends JSON, not audio: the element errors instead of playing. That is
      // a failure to speak, not a finished phrase, so it must fall through to
      // the browser's own voice rather than silently doing nothing.
      let failed = false;
      const done = new Promise<void>((resolve) => {
        a.onended = () => { if (current === a) { current = null; notify(null); } resolve(); };
        a.onerror = () => { failed = true; if (current === a) { current = null; } resolve(); };
      });
      a.onplaying = () => start(a);
      await a.play();
      // Placement listen questions pass a timeout so a stalled server stream
      // falls back to speechSynthesis (previous behaviour, now shared).
      if (opts.playMs) await Promise.race([done, new Promise((r) => setTimeout(r, opts.playMs))]);
      else await done;
      if (!failed) return;
      notify(text);
    } catch (e) {
      console.warn('server tts failed, falling back to speechSynthesis', e);
      if (current) { current = null; }
    }
  }
  if (!('speechSynthesis' in window)) { notify(null); return; }
  const u = new SpeechSynthesisUtterance(text);
  // Review and exam queues pass no lang: the account's own target (one per
  // account) is right, never a hard-coded Catalan voice for Spanish text.
  const lang = opts.lang || currentTarget() || 'ca';
  // Browser voice locales come from the server's pair table (phones ship
  // `ca-ES`/`fa-IR`/`en-US`, rarely the bare code). Before the table has
  // loaded — or for a language that is not in it — the bare code is the
  // last resort.
  u.lang = pairFor(lang)?.voiceLocale || lang;
  u.rate = 0.9;
  const v = speechSynthesis.getVoices().find((v) => v.lang.toLowerCase().startsWith(lang.toLowerCase()));
  if (v) u.voice = v;
  u.onstart = () => start();
  u.onend = u.onerror = () => { start(); if (playingText === text) notify(null); };
  opts.onDevice?.();
  speechSynthesis.speak(u);
  // The device voice has nothing to fetch: it counts as heard now (some voices never fire `onstart`).
  heardNow(text);
}
